import { Grid } from '@mui/joy'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '~/entities/common/viewer'
import { CarBrand, CarModel, Color, Subdivision } from '~/shared/api'
import {
  carPropertyTypeEnumOptions,
  transmissionEnumOptions,
} from '~/shared/config/constants'
import {
  AsyncAutocompleteInput,
  AutocompleteInput,
  DatePickerInput,
  Switch,
  TextInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'
import { LocationAsyncAutocompleteInput } from './ui/LocationAsyncAutocompleteInput'
import { RegionAsyncAutocompleteInput } from './ui/RegionAsyncAutocompleteInput'

type MainProps = {
  showSkeleton?: boolean
  disabled?: boolean
  isEditing?: boolean
  isView?: boolean
}

export function Main({ showSkeleton, disabled, isEditing, isView }: MainProps) {
  const { t } = useTranslation()
  const { canCarPropertyTypeUpdate } = usePermissions()

  const { watch, setValue } = useFormContext()

  const brand = watch('brand')
  const brandId = brand?.id

  const region = watch('regionId')
  const regionId = region?.id

  useEffect(() => {
    if (regionId && isEditing) {
      setValue('subdivisionId', null)
    }
    // eslint-disable-next-line
  }, [regionId])

  useEffect(() => {
    if (brandId && isEditing) {
      setValue('modelId', null)
    }
    // eslint-disable-next-line
  }, [brandId])

  return (
    <Container>
      <ContainerTitle>{t('basic information')}</ContainerTitle>
      <Grid container spacing={2.5}>
        <Grid xs={6}>
          <TextInput
            label='form.vin number'
            placeholder='form.enter vin number'
            name='vin'
            skeletonShow={showSkeleton}
            readOnly={disabled}
          />
        </Grid>
        <Grid xs={3}>
          <TextInput
            label='form.year of manufacture'
            name='manufactureYear'
            placeholder='form.enter year of manufacture'
            skeletonShow={showSkeleton}
            readOnly={disabled}
          />
        </Grid>
        <Grid xs={3}>
          <DatePickerInput
            label='form.date of commissioning'
            name='startExploitationDate'
            placeholder='form.select date of commissioning'
            skeletonShow={showSkeleton}
            readOnly={disabled}
          />
        </Grid>
        <Grid xs={6}>
          <AsyncAutocompleteInput
            label='form.brand'
            name='brand'
            placeholder='form.select brand'
            skeletonShow={showSkeleton}
            readOnly={disabled}
            fetchOptions={CarBrand.fetchOptions}
          />
        </Grid>
        <Grid xs={3}>
          <RegionAsyncAutocompleteInput
            showSkeleton={showSkeleton}
            disabled={disabled}
          />
        </Grid>
        <Grid xs={3}>
          <LocationAsyncAutocompleteInput
            showSkeleton={showSkeleton}
            disabled={disabled}
          />
        </Grid>
        <Grid xs={2}>
          <AsyncAutocompleteInput
            label='form.model'
            name='modelId'
            placeholder='form.select model'
            skeletonShow={showSkeleton}
            readOnly={disabled}
            fetchOptions={(search) => CarModel.fetchOptions(search, brandId)}
            queryKey={brandId ? [brandId] : []}
          />
        </Grid>
        <Grid xs={2}>
          <AutocompleteInput
            label='form.gearbox type'
            name='transmission'
            placeholder='form.select gearbox type'
            skeletonShow={showSkeleton}
            readOnly={disabled}
            options={transmissionEnumOptions}
          />
        </Grid>
        <Grid xs={2}>
          <AsyncAutocompleteInput
            label='form.color'
            name='colorId'
            placeholder='form.select color'
            skeletonShow={showSkeleton}
            readOnly={disabled}
            fetchOptions={Color.fetchOptions}
          />
        </Grid>
        <Grid xs={6}>
          <AsyncAutocompleteInput
            label='form.subdivision'
            name='subdivisionId'
            placeholder='form.select subdivision'
            skeletonShow={showSkeleton}
            readOnly={disabled}
            fetchOptions={(search) =>
              Subdivision.fetchOptionsWithRelations(search, regionId)
            }
            queryKey={regionId ? [regionId] : []}
          />
        </Grid>
        {isView && (
          <Grid xs={3}>
            <TextInput
              label='form.license plate number'
              name='plateNumber'
              placeholder='form.enter license plate number'
              skeletonShow={showSkeleton}
              readOnly
            />
          </Grid>
        )}
        <Grid xs={isView ? 3 : 6}>
          <AutocompleteInput
            label='form.type of ownership'
            name='propertyType'
            placeholder='form.select type of ownership'
            skeletonShow={showSkeleton}
            readOnly={disabled || !canCarPropertyTypeUpdate}
            options={carPropertyTypeEnumOptions}
            clearOnBlur
          />
        </Grid>
        <Grid xs={2}>
          <Switch
            labelI18n='form.buyout'
            name='isLeasingFullyPaid'
            formControlProps={{ sx: { marginTop: '40px' } }}
            skeletonShow={showSkeleton}
            readOnly={disabled}
          />
        </Grid>
        <Grid xs={2}>
          <Switch
            labelI18n='form.pre total'
            name='isPreTotaled'
            formControlProps={{ sx: { marginTop: '40px' } }}
            skeletonShow={showSkeleton}
            readOnly={disabled}
          />
        </Grid>
        <Grid xs={2}>
          <Switch
            labelI18n='form.participates in promotion'
            name='isPromotion'
            formControlProps={{ sx: { marginTop: '40px' } }}
            skeletonShow={showSkeleton}
            readOnly={disabled}
          />
        </Grid>
      </Grid>
    </Container>
  )
}
